/**
 * @file Public landing page — pages/Landing/AboutPage.tsx.
 */

import React from "react";
import { Link } from "react-router-dom";
import "./LandingPage.css";
import "./ContentPage.css";

const AboutPage: React.FC = () => {
  return (
    <div className="landing-root">
      {/* Top Header */}
      <header className="landing-header flex justify-center items-center space-x-3 text-center py-4">
        <h1 className="landing-main-title text-4xl font-bold text-primary">
          ICoMEC
        </h1>
        <h2 className="landing-sub-title text-xl text-grayDark">
          Indian Construction Materials Embodied Carbon Data Base
        </h2>
      </header>

      {/* Navigation Bar */}
      <nav className="landing-nav">
        <Link to="/">Home</Link>
        <Link to="/join">Join</Link>
        <Link to="/impact">Impact</Link>
        <Link to="/research">Research and Analysis</Link>
        <Link to="/about">About</Link>
      </nav>

      {/* Content Section */}
      <section className="content-page">
        <h1 className="content-title">About</h1>
        
        <div className="content-section">
          <h2>Empowering India's Sustainable Growth</h2>
          <p>
            ICoMEC was created to provide a dedicated data infrastructure for the Indian built environment. We provide transparent, contextual, and decision-ready embodied carbon data designed specifically for the unique construction landscape of India.
          </p>
          <p>
            Our platform serves as a bridge between complex environmental science and actionable design, ensuring that every stakeholder has the tools they need to build a lower-carbon future with confidence.
          </p>
        </div>

        <div className="content-section">
          <h2>Context, Clarity, and Confidence</h2>
          <p>
            ICoMEC is built to translate raw data into meaningful insights. We focus on three critical areas to support informed decision-making:
          </p>
          <ul className="content-list">
            <li><strong>Localized Context:</strong> We provide data that reflects the specific energy mixes, material supply chains, and manufacturing realities of the Indian industry.</li>
            <li><strong>Communicated Uncertainty:</strong> We believe in complete honesty. By sharing the statistical range and variance of our data, we ensure users understand the nuance behind every number.</li>
            <li><strong>Stakeholder Accessibility:</strong> Our interface and reports are designed to be interpreted by everyone, from sustainability experts to architects and policymakers, making carbon data a universal language.</li>
          </ul>
        </div>

        <div className="content-section">
          <h2>Rigor Without Compromise</h2>
          <p>
            Our methodology balances scientific depth with practical application. We guide our development by two core principles:
          </p>
          <ul className="content-list">
            <li><strong>Scientific Rigor:</strong> Every data point is grounded in the highest standards of Life Cycle Assessment (LCA) methodology.</li>
            <li><strong>Transparency Over Precision:</strong> We value honest communication of data variability. Our priority is providing a clear, traceable path for every calculation, allowing for truly justifiable climate actions.</li>
          </ul>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-main">
          <div className="footer-left">
            <h2>ICoMEC</h2>
            <p>
              Indian Construction Materials Embodied
              <br /> Carbon Data Base
            </p>
          </div>

          <div className="footer-links">
            <div>
              <h4>About Us</h4>
              <ul>
                <li><Link to="/who-we-are">Who We Are</Link></li>
                <li><Link to="/what-we-do">What We Do</Link></li>
                <li><Link to="/leadership">Leadership</Link></li>
                <li><Link to="/partners">Partners</Link></li>
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/glossary">Glossary</Link></li>
              </ul>
            </div>
            <div>
              <h4>Resources</h4>
              <ul>
                {/* <li><Link to="/resource-library">Resource Library</Link></li> */}
                <li><Link to="/events">Events</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <p>
          <Link to="/cookies-policy">Cookies Policy</Link> · <Link to="/privacy-policy">Privacy Policy</Link> · <Link to="/legal">Legal</Link> · <Link to="/terms-of-use">Terms of Use</Link>
          {/* · <Link to="/imprint">Imprint</Link> */}
        </p>
        <p>© 2026 CARBSE. All rights reserved.</p>
      </div>
    </div>
  );
};

/** React component — About Page. */
export default AboutPage;
